import { type ReactNode } from "react";

import { BTText } from "@btnet/core-components";
import type { CustomFieldAssociatedType } from "@btnet/services-btnet/handlers/models/customFieldAssociatedType";
import type { DropdownCustomFieldDefinition } from "@btnet/services-btnet/handlers/models/dropdownCustomFieldDefinition";
import type { DropdownCustomFieldValue } from "@btnet/services-btnet/handlers/models/dropdownCustomFieldValue";

import { friendlyDefaultDisplayValue } from "utilities/helpers";

import { BTSelect } from "commonComponents/btWrappers/BTSelect/BTSelect";
import { DropdownCellEditor } from "commonComponents/entity/customField/utils/customFieldTypes/DropdownCellEditor";
import {
    getOptionsFromDefinition,
    getOptionSortOrder,
    getOptionText,
} from "commonComponents/entity/customField/utils/customFieldTypes/selectCustomFieldUtils";
import {
    defaultRenderAgGridCell,
    type ICustomFieldTypeHandler,
} from "commonComponents/entity/customField/utils/customFieldValues.util";

export const DropdownCustomFieldHandler: ICustomFieldTypeHandler<
    number,
    DropdownCustomFieldValue,
    DropdownCustomFieldDefinition
> = {
    getValue(field): number | null {
        const id = field.selectedOptionId;
        return id != null && id !== -1 ? id : null;
    },

    setValue(field, value): DropdownCustomFieldValue {
        return { ...field, selectedOptionId: value ?? -1 };
    },

    createValue(
        customFieldId: number,
        associatedType: CustomFieldAssociatedType,
        associatedTypeId: number,
        value: number | null,
    ): DropdownCustomFieldValue {
        return {
            dataType: "DropDownList",
            customFieldId,
            customFieldValueId: 0,
            associatedType,
            associatedTypeId,
            selectedOptionId: value ?? -1,
        };
    },

    compare(valueA: number | null, valueB: number | null, definition?, sortByOptionOrder?): number {
        if (sortByOptionOrder) {
            const sortOrderA = valueA != null ? getOptionSortOrder(valueA, definition) : -1;
            const sortOrderB = valueB != null ? getOptionSortOrder(valueB, definition) : -1;
            return sortOrderA - sortOrderB;
        }
        const textA = valueA != null ? getOptionText(valueA, definition) : "";
        const textB = valueB != null ? getOptionText(valueB, definition) : "";
        return textA.localeCompare(textB, undefined, { sensitivity: "base" });
    },

    renderDisplay(value: number | null, definition): ReactNode {
        if (value == null || value === -1) {
            return <BTText size="normal-md" text={friendlyDefaultDisplayValue} />;
        }
        const displayText = getOptionText(value, definition);
        return <BTText size="normal-md" text={displayText} />;
    },

    renderEditor(
        value: number | null,
        customFieldId: number,
        onChange: (newValue: number | null) => void,
        onBlur: () => void,
        _autoFocus?: boolean,
        definition?: DropdownCustomFieldDefinition,
        _field?: DropdownCustomFieldValue,
        formMode?: "always-editing" | "click-to-edit",
    ): ReactNode {
        const treeData = getOptionsFromDefinition(definition);
        const selectedValues = value != null && value !== -1 ? [value] : undefined;

        return (
            <BTSelect
                id={`dropdownCustomFieldEditor-${customFieldId}`}
                data-testid="dropdownCustomFieldEditor"
                value={selectedValues}
                treeData={treeData}
                onChange={(_id, val) => {
                    onChange(typeof val === "number" ? val : null);
                }}
                onBlur={() => onBlur()}
                multiple={false}
                formMode={formMode ?? "click-to-edit"}
                placeholder={friendlyDefaultDisplayValue}
            />
        );
    },

    getAgGridCellEditor() {
        return DropdownCellEditor;
    },

    renderAgGridCell(context): ReactNode {
        return defaultRenderAgGridCell(context);
    },
};
